import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/ui/table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CheckCircle, Clock, Edit, MoreHorizontal } from "lucide-react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import LoggerContext from "../../../../context/LoggerProvider";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { config } from "../../../../Environment";
import { Input } from "../../../../components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../../../components/ui/pagination";
import SingleEngagement from "./singleEngagement";

const itemsPerPage = 5;
const ENGAGEMENT_HISTORY_STORAGE_KEY = "engagementHistoryModalState";

const EngagementHistoryModal = ({
  engagementHistoryModal,
  setEngagementHistoryModal,
  prescription,
  setPrescriptions,
  refreshLists,
}: any) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from localStorage or URL params
  const getInitialPage = () => {
    try {
      const stored = localStorage.getItem(ENGAGEMENT_HISTORY_STORAGE_KEY);
      if (stored) {
        const { prescriptionId, page } = JSON.parse(stored);
        if (prescriptionId === prescription?._id) {
          return page;
        }
      }
    } catch (error) {
      console.error("Error reading engagement history state from localStorage:", error);
    }
    return parseInt(searchParams.get("ehPage") || "1", 10);
  };

  const [currentPage, setCurrentPage] = useState(getInitialPage());

  const processCount = Array.isArray(prescription?.processes) ? prescription?.processes.length : 0;
  const totalPages = processCount > 0 ? Math.ceil(processCount / itemsPerPage) : 1;

  const { t } = useTranslation();
  const { Logger } = useContext(LoggerContext);
  const location = useLocation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();

  const currentEngagements = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const processCount = Array.isArray(prescription?.processes) ? prescription?.processes.length : 0;
    if (processCount === 0) return [];

    // Sort processes by lastUpdated in descending order (most recent first)
    const sortedProcesses = [...prescription.processes].sort((a, b) => {
      const timeA = a.lastUpdated || 0;
      const timeB = b.lastUpdated || 0;
      return timeB - timeA; // Descending order
    });

    return sortedProcesses.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, prescription?.processes, itemsPerPage]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);

    // Update localStorage when page changes
    try {
      const state = {
        prescriptionId: prescription?._id,
        page: page,
        isOpen: true,
      };
      localStorage.setItem(ENGAGEMENT_HISTORY_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error("Error saving engagement history state to localStorage:", error);
    }

    // Update URL params when page changes
    const newParams = new URLSearchParams(searchParams);
    newParams.set("ehPage", page.toString());
    newParams.set("ehPrescriptionId", prescription?._id || "");
    setSearchParams(newParams, { replace: true });
  };

  const getPageNumbers = () => {
    const range: (number | "...")[] = [];
    const delta = 1;

    const left = Math.max(2, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    range.push(1);

    if (left > 2) range.push("...");

    for (let i = left; i <= right; i++) {
      range.push(i);
    }

    if (right < totalPages - 1) range.push("...");

    if (totalPages > 1) range.push(totalPages);

    return range;
  };

  const pageNumbers = useMemo(getPageNumbers, [currentPage, totalPages]);

  // Sync localStorage and URL params with modal state when modal is opened/closed externally
  useEffect(() => {
    if (engagementHistoryModal && prescription?._id) {
      // When modal opens, save to localStorage and add URL params
      try {
        const state = {
          prescriptionId: prescription._id,
          page: currentPage,
          isOpen: true,
        };
        localStorage.setItem(ENGAGEMENT_HISTORY_STORAGE_KEY, JSON.stringify(state));
      } catch (error) {
        console.error("Error saving engagement history state to localStorage:", error);
      }

      const newParams = new URLSearchParams(searchParams);
      newParams.set("ehPrescriptionId", prescription._id);
      newParams.set("ehPage", currentPage.toString());
      setSearchParams(newParams, { replace: true });
    } else if (!engagementHistoryModal) {
      // When modal closes, clear localStorage and remove URL params
      try {
        localStorage.removeItem(ENGAGEMENT_HISTORY_STORAGE_KEY);
      } catch (error) {
        console.error("Error removing engagement history state from localStorage:", error);
      }

      const newParams = new URLSearchParams(searchParams);
      if (newParams.has("ehPrescriptionId")) {
        newParams.delete("ehPrescriptionId");
        newParams.delete("ehPage");
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [engagementHistoryModal, prescription?._id]);

  // Sync page number from URL params when they change
  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get("ehPage") || "1", 10);
    if (pageFromUrl !== currentPage && pageFromUrl >= 1 && pageFromUrl <= totalPages) {
      setCurrentPage(pageFromUrl);
    }
  }, [searchParams]);

  const handleOpenChange = (open: boolean) => {
    setEngagementHistoryModal(open);

    // If closing manually, clean up localStorage and URL params
    if (!open) {
      try {
        localStorage.removeItem(ENGAGEMENT_HISTORY_STORAGE_KEY);
      } catch (error) {
        console.error("Error removing engagement history state from localStorage:", error);
      }

      const newParams = new URLSearchParams(searchParams);
      newParams.delete("ehPrescriptionId");
      newParams.delete("ehPage");
      setSearchParams(newParams, { replace: true });
    }
  };

  return (
    <>
      <Dialog
        open={engagementHistoryModal}
        onOpenChange={handleOpenChange}
      >
        <DialogContent className="max-w-4xl px-2 md:px-6">
          <DialogHeader>
            <DialogTitle>{t("engagement_history")}</DialogTitle>
            <DialogDescription>
              {t("engagementHistoryDialogTitle", { name: prescription?.name })}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <div className="border rounded-md overflow-hidden">
              {/* Single unified table with horizontal scroll  */}
              <div className="overflow-x-auto">
                <Table>
                  {/* Header with sticky positioning */}
                  <TableHeader className="sticky top-0 z-10 bg-background">
                    <TableRow>
                      <TableHead className="min-w-[200px] w-[40%] border-b-[1px]">
                        {t("engagement_name")}
                      </TableHead>
                      <TableHead className="min-w-[120px] w-[20%] hidden md:table-cell">
                        {t("status")}
                      </TableHead>
                      <TableHead className="min-w-[150px] w-[25%] hidden md:table-cell">
                        {t("last_update")}
                      </TableHead>
                      <TableHead className="min-w-fit md:w-[15%] text-right hidden md:table-cell">
                        {t("actions")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>

                  {/* Body with vertical scroll */}
                  <TableBody className="overflow-y-auto h-64">
                    {currentEngagements?.map((process: any) => (
                      <SingleEngagement
                        key={process._id}
                        process={process}
                        refreshLists={refreshLists}
                        setPrescriptions={setPrescriptions}
                      />
                    ))}
                  </TableBody>

                  {/* Footer with pagination - sticky at bottom */}
                  {prescription?.processes?.length > 0 && (
                    <TableFooter className="sticky bottom-0 z-10 bg-background border-t">
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="text-right px-0 py-2 border-none"
                        >
                          <Pagination>
                            <PaginationContent className="justify-center space-x-1">
                              {/* Previous */}
                              <PaginationItem>
                                <PaginationPrevious
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange(currentPage - 1);
                                  }}
                                  aria-disabled={currentPage === 1}
                                />
                              </PaginationItem>

                              {/* Page Numbers (Only on md and above) */}
                              <div className="hidden md:flex">
                                {pageNumbers.map((page, idx) =>
                                  page === "..." ? (
                                    <PaginationItem key={`ellipsis-${idx}`}>
                                      <PaginationEllipsis />
                                    </PaginationItem>
                                  ) : (
                                    <PaginationItem key={page}>
                                      <PaginationLink
                                        href="#"
                                        isActive={currentPage === page}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handlePageChange(page);
                                        }}
                                      >
                                        {page}
                                      </PaginationLink>
                                    </PaginationItem>
                                  )
                                )}
                              </div>

                              {/* Next */}
                              <PaginationItem>
                                <PaginationNext
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange(currentPage + 1);
                                  }}
                                  aria-disabled={currentPage === totalPages}
                                />
                              </PaginationItem>
                            </PaginationContent>
                          </Pagination>
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              </div>

              {/* Empty state message */}
              {prescription?.processes?.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">
                    {t("engagementHistoryDialogEmptyMessage")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EngagementHistoryModal;
